import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TargetCostData } from '@/types/target-cost';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/lib/utils';

interface CostsFormProps {
  costs: TargetCostData['costs'];
  onChange: (updates: Partial<TargetCostData['costs']>) => void;
}

export function CostsForm({ costs, onChange }: CostsFormProps) {
  const { totalPrices, finalPWDD, costOverrun, savings } = costs;

  // Track whether user is actively editing each field
  const [isEditingTotalPrices, setIsEditingTotalPrices] = useState(false);
  const [isEditingFinalPWDD, setIsEditingFinalPWDD] = useState(false);

  // Local state for the text values during editing
  const [totalPricesText, setTotalPricesText] = useState<string>('');
  const [finalPWDDText, setFinalPWDDText] = useState<string>('');

  // Formatted display values derived from props (used when not editing)
  const formattedTotalPrices = useMemo(() =>
    totalPrices != null ? Number(totalPrices).toLocaleString('en-US') : '',
    [totalPrices]
  );

  const formattedFinalPWDD = useMemo(() =>
    finalPWDD != null ? Number(finalPWDD).toLocaleString('en-US') : '',
    [finalPWDD]
  );

  // Validation warnings (not blocking - just informational)
  const totalPricesWarning = useMemo(() => {
    if (totalPrices != null && totalPrices < 0) {
      return 'Value should not be negative';
    }
    return null;
  }, [totalPrices]);

  const finalPWDDWarning = useMemo(() => {
    if (finalPWDD != null && finalPWDD < 0) {
      return 'Value should not be negative';
    }
    return null;
  }, [finalPWDD]);

  /**
   * Derived calculation for Cost Overrun.
   * If finalPWDD > totalPrices, it equals finalPWDD - totalPrices, otherwise 0.
   */
  const computedCostOverrun = useMemo(() => {
    if (totalPrices == null || finalPWDD == null) return null;
    return finalPWDD > totalPrices ? finalPWDD - totalPrices : 0;
  }, [totalPrices, finalPWDD]);

  /**
   * Derived calculation for Savings.
   * If totalPrices > finalPWDD, it equals totalPrices - finalPWDD, otherwise 0.
   */
  const computedSavings = useMemo(() => {
    if (totalPrices == null || finalPWDD == null) return null;
    return totalPrices > finalPWDD ? totalPrices - finalPWDD : 0;
  }, [totalPrices, finalPWDD]);

  /**
   * When totalPrices or finalPWDD changes, update the parent's state
   * with the computed derived values.
   */
  useEffect(() => {
    if (totalPrices != null && finalPWDD != null) {
      if (costOverrun !== computedCostOverrun || savings !== computedSavings) {
        onChange({
          costOverrun: computedCostOverrun,
          savings: computedSavings,
        });
      }
    }
  }, [
    totalPrices,
    finalPWDD,
    computedCostOverrun,
    computedSavings,
    costOverrun,
    savings,
    onChange
  ]);

  /**
   * Handles focus on the totalPrices field.
   * Sets editing mode and initializes the text with raw value.
   */
  const handleTotalPricesFocus = useCallback(() => {
    setIsEditingTotalPrices(true);
    setTotalPricesText(totalPrices != null ? String(totalPrices) : '');
  }, [totalPrices]);

  /**
   * Handles changes to the totalPrices field.
   * It removes commas from the input, updates the local text state,
   * and propagates the raw numeric value to the parent state.
   */
  const handleTotalPricesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      // Remove commas so the raw number can be parsed.
      const sanitized = input.replace(/,/g, '');
      setTotalPricesText(sanitized);
      const parsed = sanitized !== '' ? parseFloat(sanitized) : null;
      onChange({ totalPrices: parsed });
    },
    [onChange]
  );

  /**
   * On blur, exit editing mode to show formatted value.
   */
  const handleTotalPricesBlur = useCallback(() => {
    setIsEditingTotalPrices(false);
  }, []);

  /**
   * Handles focus on the finalPWDD field.
   * Sets editing mode and initializes the text with raw value.
   */
  const handleFinalPWDDFocus = useCallback(() => {
    setIsEditingFinalPWDD(true);
    setFinalPWDDText(finalPWDD != null ? String(finalPWDD) : '');
  }, [finalPWDD]);

  /**
   * Handles changes to the finalPWDD field.
   * It removes commas, updates the local text state,
   * and propagates the raw numeric value to the parent.
   */
  const handleFinalPWDDChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const sanitized = input.replace(/,/g, '');
      setFinalPWDDText(sanitized);
      const parsed = sanitized !== '' ? parseFloat(sanitized) : null;
      onChange({ finalPWDD: parsed });
    },
    [onChange]
  );

  /**
   * On blur, exit editing mode to show formatted value.
   */
  const handleFinalPWDDBlur = useCallback(() => {
    setIsEditingFinalPWDD(false);
  }, []);

  /**
   * Renders a tooltip icon with the provided content.
   */
  const renderTooltip = useCallback(
    (content: string) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">{content}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
    []
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Prices Field */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="totalPrices">The Total of the Prices (£)</Label>
            {renderTooltip("Enter the total agreed contract price for the project.")}
          </div>
          <Input
            id="totalPrices"
            type="text"
            value={isEditingTotalPrices ? totalPricesText : formattedTotalPrices}
            onFocus={handleTotalPricesFocus}
            onChange={handleTotalPricesChange}
            onBlur={handleTotalPricesBlur}
            placeholder="Enter total prices"
            className={cn(
              "text-right",
              totalPricesWarning && "border-amber-500 focus-visible:ring-amber-500"
            )}
            inputMode="numeric"
            aria-describedby={totalPricesWarning ? "totalPrices-warning" : undefined}
          />
          {totalPricesWarning && (
            <p id="totalPrices-warning" className="flex items-center gap-1.5 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              {totalPricesWarning}
            </p>
          )}
        </div>

        {/* Final PWDD Field */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="finalPWDD">Final PWDD (£)</Label>
            {renderTooltip("Enter the final Price for Work Done to Date, including Defined Cost and Fee.")}
          </div>
          <Input
            id="finalPWDD"
            type="text"
            value={isEditingFinalPWDD ? finalPWDDText : formattedFinalPWDD}
            onFocus={handleFinalPWDDFocus}
            onChange={handleFinalPWDDChange}
            onBlur={handleFinalPWDDBlur}
            placeholder="Enter final PWDD"
            className={cn(
              "text-right",
              finalPWDDWarning && "border-amber-500 focus-visible:ring-amber-500"
            )}
            inputMode="numeric"
            aria-describedby={finalPWDDWarning ? "finalPWDD-warning" : undefined}
          />
          {finalPWDDWarning && (
            <p id="finalPWDD-warning" className="flex items-center gap-1.5 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              {finalPWDDWarning}
            </p>
          )}
        </div>
      </div>

      {/* Savings / Cost Overrun Display */}
      {computedSavings !== null && computedCostOverrun !== null && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
          {/* Savings */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-green-600" />
              <span className="font-medium">Savings</span>
            </div>
            <span className={`text-lg font-semibold ${computedSavings > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
              {formatCurrency(computedSavings)}
            </span>
          </div>

          {/* Cost Overrun */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-destructive" />
              <span className="font-medium">Cost Overrun</span>
            </div>
            <span className={`text-lg font-semibold ${computedCostOverrun > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {formatCurrency(computedCostOverrun)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}